import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  useMediaQuery,
  Button,
  Divider,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import axios from 'axios';
import { useSelection } from '../../../../components/context/SelectionContext';

interface EditPaysModalWebProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  payId: string | null;
  payDate: string | null;
  payment: number | null;
  balance: number | null;
  fullname: string | null;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const EditPaysModalWeb: React.FC<EditPaysModalWebProps> = ({
  open,
  onClose,
  clientId,
  payId,
  payment,
  balance,
  fullname,
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user_id, setUser_id } = useSelection();

  // ESTADOS INICIALES
  const [valorAbono, setValorAbono] = useState(0);
  const [selectedFormPay, setSelectedFormPay] = useState('1');
  const [selectedBank, setSelectedBank] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [location, setLocation] = useState<string | null>(null);

  const banks = [
    { id: '1', name: 'Banco A' },
    { id: '2', name: 'Banco B' },
    { id: '3', name: 'Banco C' },
  ];
  const syncCoordinates = async () => {
    try {
      const coords = await getCoordinates();
      setLocation(coords);
      return coords;
    } catch (error) {
      setLocation(null);
      throw error;
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    syncCoordinates().catch(() => {
      showAlert('No se pudo obtener la ubicación actual. El abono se actualizará sin coordenadas.', 'warning');
    });
  }, [open]);
  useEffect(() => {
    (
      async () => {
        const {data} = await axios.get('user')
        setUser_id(data.data.id)
      }
    )();
  },[]);

  // CUANDO SE ABRA EL MODAL, REINICIAR LOS ESTADOS
  useEffect(() => {
    if (open) {
      setValorAbono(payment || 0);
      setSelectedFormPay('1');
      setSelectedBank('');
      setReceiptNumber('');
      setErrorMessage('');
    }
  }, [open, payment]);

  // FUNCION PARA CERRAR Y LIMPIAR EL MODAL
  const handleClose = () => {
    setValorAbono(0);
    setSelectedFormPay('1');
    setSelectedBank('');
    setReceiptNumber('');
    setErrorMessage('');
    onClose(); // CERRAR EL MODAL
  };

  // FUNCION PARA GUARDAR LOS CAMBIOS
  const getCoordinates = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const coordinates = `${lat}, ${lng}`;
            resolve(coordinates);
          },
          error => {
            reject(error);
          }
        );
      } else {
        showAlert('Geolocation is not supported by this browser.', 'warning');
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };
  const handleAccept = async () => {
    if (selectedFormPay === '0' && (!selectedBank || !receiptNumber)) {
      setErrorMessage('Debe seleccionar un banco y proporcionar el número de recibo.');
      return;
    }

    let currentLocation = location;
    if (!currentLocation) {
      try {
        currentLocation = await syncCoordinates();
      } catch (error) {
        currentLocation = null;
        showAlert('No se pudieron obtener las coordenadas. Se actualizará el abono sin ubicación.', 'warning');
      }
    }

    const formData = {
      value: valorAbono,
      user_id,
      cash: selectedFormPay === '1',
      bank_id: selectedFormPay === '0' ? selectedBank : null,
      receipt_bank: selectedFormPay === '0' ? receiptNumber : null,
      location: currentLocation,
      state: true,
    };
    try {
      setIsSaving(true);
      showLoading();
      await axios.put(`pays/${payId}`, formData);
      showAlert('Se ha actualizado el abono exitosamente!', 'success');
      handleClose(); // CERRAR Y LIMPIAR EL MODAL DESPUÉS DE GUARDAR
    } catch (error) {
      showAlert('Error actualizando el abono!', 'error');
    } finally {
      setIsSaving(false);
      hideLoading();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose} // 🔥 CERRAR Y LIMPIAR
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isSmallScreen ? '80%' : 800,
        height: selectedFormPay === '0' ? (isSmallScreen ? 'auto' : 520) : (isSmallScreen ? 'auto' : 400),
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        maxHeight: '80vh',
        overflowY: 'auto',
      }}>
        <Box>
          <Typography variant="h4" component="h2" gutterBottom>
            Editar Abono
          </Typography>
          <Typography variant="body1" gutterBottom>
            Editar abono No {payId} del cliente: {fullname} - Nit: {clientId}
          </Typography>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <Divider sx={{ mb: 2 }} />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Forma de Pago</InputLabel>
            <Select
              value={selectedFormPay}
              onChange={(e) => setSelectedFormPay(e.target.value)}
              label="Forma de Pago"
            >
              <MenuItem value="1">Efectivo</MenuItem>
              <MenuItem value="0">Transferencia</MenuItem>
            </Select>
          </FormControl>

          {selectedFormPay === '0' && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Banco</InputLabel>
                <Select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  label="Banco"
                >
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Nro Recibo"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}

          <TextField
            fullWidth
            label="Valor Abono"
            value={valorAbono}
            onChange={(e) => setValorAbono(Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button variant="outlined" onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleAccept} disabled={isSaving}>Guardar</Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditPaysModalWeb;
